<template>
  <v-container fluid>
    <v-row>
      <v-col class="py-0">
        <v-container fluid>
          <v-row justify="end" align="center">
            <ag-grid-switch
              :name="refNameColumns"
              class="mr-3"
            ></ag-grid-switch>
            <div>
              <v-select
                v-if="showGroupedView"
                :items="groupByOptions"
                v-model="selectedGroupedBy"
                label="Group by"
                item-text="name"
                return-object
                :outlined="outlinedInputs"
                hide-details
                dense
                class="mx-2"
              ></v-select>
            </div>
            <v-switch
              prepend-icon="mdi-view-list"
              class="mr-3"
              label="Group View"
              v-model="showGroupedView"
            ></v-switch>
            <download-csv :data="csvData" delimiter=",">
              <v-btn small class="mr-1"
                ><v-icon left small>mdi-file-delimited</v-icon> Get CSV</v-btn
              >
            </download-csv>
            <list-visible-columns-and-filter
              :visible-columns="visibleColumns"
              :selectable-headers="selectableHeaders"
              v-model="search"
              @change="onVisibleColumnsChange"
            >
            </list-visible-columns-and-filter>
          </v-row>
        </v-container>
      </v-col>
    </v-row>

    <div v-if="showAgGrid" key="ag-grid">
      <v-card outlined>
        <ag-grid-vue
          class="ag-theme-alpine"
          id="ag-it-details-items-list"
          :columnDefs="agGridColumns"
          :rowData="itemsWithExtras"
          rowSelection="multiple"
          :context="agContext"
          @grid-ready="onAgGridReady"
          @columnMoved="onAgColumnMoved"
          @columnResized="onAgColumnResized"
          @sortChanged="onAgSortChanged"
          @selection-changed="onAgSelectionChanged"
          @newColumnsLoaded="onAgNewColumns"
          suppressDragLeaveHidesColumns
        >
        </ag-grid-vue>
      </v-card>
    </div>
    <v-row v-else>
      <v-col v-if="showGroupedView">
        <list-grouped :group="selectedGroupedBy" :items="itemsWithExtras">
          <template v-slot:table="{ items }">
            <v-card>
              <iti-details-items-table
                :items="items"
                :headers="filteredHeaders"
                :search="search"
                @showRemarks="onShowRemarks"
                @showItemPricingEditor="onShowItemPricingEditor"
              ></iti-details-items-table>
            </v-card>
          </template>
        </list-grouped>
      </v-col>
      <v-col v-else>
        <v-card outlined>
          <v-data-table
            :headers="filteredHeaders"
            :items="itemsWithExtras"
            :search="search"
            v-bind="pagingOptions"
            :expanded.sync="expanded"
            :sort-by="sortBy"
            :height="gridHeight"
            :fixed-header="fixedHeader"
            ><template v-slot:item.margin="{ item }">
              {{ item.margin | priceLabeler }}
            </template>
            <template v-slot:item.markup="{ item }">
              {{ item.markup | priceLabeler }}
            </template>
            <template v-slot:item.totalCostInBase="{ item }">
              {{ item.totalCostInBase | priceLabeler }}
            </template>
            <template v-slot:item.pricing.totalBuy="{ item }">
              {{ item.pricing.totalBuy | priceLabeler }}
            </template>

            <template v-slot:item.netProfit="{ item }">
              {{ item.netProfit | priceLabeler }}
            </template>
            <template v-slot:item.profit="{ item }">
              {{ item.profit | priceLabeler }}
            </template>

            <template v-slot:item.commission="{ item }">
              {{ item.commission | priceLabeler }}
            </template>

            <template v-slot:item.totalPrice="{ item }">
              {{ item.totalPrice | priceLabeler }}
            </template>
            <template v-slot:item.totalSellInBase="{ item }">
              {{ item.totalSellInBase | priceLabeler }}
            </template>
            <template v-slot:item.toms="{ item }">
              {{ item.toms | priceLabeler }}
            </template>
            <template v-slot:item.node.serviceTypeName="{ item }">
              <v-icon>{{ supplierTypeIcon(item.serviceTypeName) }}</v-icon>
            </template>
            <template v-slot:item.serviceTypeName="{ item }">
              <v-icon>{{ supplierTypeIcon(item.serviceTypeName) }}</v-icon>
            </template>
            <template v-slot:item.startDate="{ item }">
              {{ item.startDate | formatDate }}
            </template>
            <template v-slot:item.endDate="{ item }">
              {{ item.endDate | formatDate }}</template
            >
            <template v-slot:item.serviceName="{ item }">
              <v-row v-if="item.isExtra" align="center">
                <v-icon color="yellow darken-2">mdi-star</v-icon>
                {{ item.boardBasis }} ({{ item.serviceName }})
              </v-row>
              <span v-else>{{ item.serviceName }}</span>
            </template>
            <template v-slot:item.isIncluded="{ item }">
              <v-checkbox
                disabled
                class="pa-0 ma-0"
                v-model="item.isIncluded"
                hide-details
              ></v-checkbox>
            </template>
            <template v-slot:item.isHidden="{ item }">
              <v-checkbox
                disabled
                class="pa-0 ma-0"
                v-model="item.isHidden"
                hide-details
              ></v-checkbox>
            </template>
            <template v-slot:item.isOptional="{ item }">
              <v-checkbox
                disabled
                class="pa-0 ma-0"
                v-model="item.isOptional"
                hide-details
              ></v-checkbox
            ></template>
            <template v-slot:item.historic="{ item }">
              <v-checkbox
                disabled
                class="pa-0 ma-0"
                v-model="item.historic"
                hide-details
              ></v-checkbox>
            </template>
            <template v-slot:item.actions="{ item }">
              <itinerary-details-items-list-actions
                :service-type="item.serviceTypeName"
                :item-is-extra="item.isExtra"
                :item="item"
                @showRemarks="onShowRemarks"
                @showItemPricingEditor="onShowItemPricingEditor"
              />
            </template>
            <template v-slot:footer>
              <v-layout justify-center pa-1 v-if="itinerary">
                <span
                  >Total Cost in Base:
                  <strong>{{ totalCostInBase | priceLabeler }}</strong></span
                ><span
                  >, Total Sell in Base:
                  <strong>{{ totalSellInBase | priceLabeler }}</strong></span
                ><span
                  >, Total Sell:
                  <strong
                    >{{ itinerary.totalPrice | priceLabeler }}
                    {{ itinerary.currencyCode }}</strong
                  ></span
                >
                <span v-if="visibleColumns.indexOf('profit') > -1"
                  >, Gross Profit:
                  <strong v-if="itinerary && itinerary.profit"
                    >{{ itinerary.profit.grossProfit | priceLabeler }}
                    {{ itinerary.currencyCode }}</strong
                  ></span
                >
                <span v-if="visibleColumns.indexOf('netProfit') > -1"
                  >, Net Profit:
                  <strong v-if="itinerary && itinerary.profit"
                    >{{ itinerary.profit.netProfit | priceLabeler }}
                    {{ itinerary.currencyCode }}</strong
                  ></span
                >
                <span v-if="visibleColumns.indexOf('margin') > -1"
                  >, Net Margin:
                  <strong v-if="itinerary && itinerary.profit"
                    >{{ itinerary.profit.margin | priceLabeler }} %</strong
                  ></span
                >
                <span v-if="visibleColumns.indexOf('markup') > -1"
                  >, Net Markup:
                  <strong v-if="itinerary && itinerary.profit"
                    >{{ itinerary.profit.markup | priceLabeler }} %</strong
                  ></span
                >
                <span v-if="visibleColumns.indexOf('commission') > -1"
                  >, Agent Net Commission:
                  <strong v-if="itinerary && itinerary.profit"
                    >{{ itinerary.profit.netCommission | priceLabeler }}
                    {{ itinerary.currencyCode }}</strong
                  ></span
                >
                <span v-if="visibleColumns.indexOf('toms') > -1"
                  >, TOMS:
                  <strong>{{ totalToms | priceLabeler }} </strong></span
                >
              </v-layout>
            </template>
            <v-alert
              slot="no-results"
              :value="true"
              color="error"
              icon="warning"
              >Your search for "{{ search }}" found no results.</v-alert
            >
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
    <v-row align="center" justify="center">
      <v-btn @click="onSupplierNotes">
        <v-icon left>mdi-note-outline</v-icon>Supplier Notes</v-btn
      >
    </v-row>
    <remarks-edit-dialog
      v-model="selectedItem"
      @close="showRemarksEditor = false"
      :show="showRemarksEditor"
      read-only
    />
    <item-pricing-dialog
      :show="showItemPricingEditor"
      :selected-item="selectedItem"
      :headerProps="headerProps"
      @onCancel="showItemPricingEditor = false"
      read-only
    ></item-pricing-dialog>
    <supplier-iti-notes-dialog
      v-if="itinerary && itinerary.id"
      v-model="itinerary"
      :iti-items="itemsWithExtras"
      @close="showSupplierNotes = false"
      :show="showSupplierNotes"
      read-only
    />

    <div v-if="debug">{{ selectedItem }}</div>
  </v-container>
</template>

<script>
import { SupplierTypeIconMixin } from "../../../mixins/SupplierTypeIconMixin";
import {
  sortByDate,
  pagingOptions,
  removeGQLTypename,
  extraDisplayQty,
  getTotal,
  getValueByPath,
  itiItemDuration,
  sortByDateWithFormat,
} from "../../../helpers/index";
import ListVisibleColumnsAndFilter from "../shared/ListVisibleColumnsAndFilter";
import { ListMixin } from "../../../mixins/ListMixin";
import { AgGridVue } from "ag-grid-vue";
import AgGridListMixin from "../../../mixins/AgGridListMixin";
import AgGridSwitch from "../shared/AgGridSwitch.vue";
import ListGrouped from "../shared/ListGrouped.vue";
import ItiDetailsItemsTable from "./ItineraryDetailsItemsTable.vue";
import RemarksEditDialog from "./builder/tools/RemarksEditDialog.vue";
import ItemPricingDialog from "./builder/tools/ItemPricingDialog.vue";
import { fromItineryItem } from "./builder/tools/helpers.js";
import ItineraryDetailsItemsListActions from "./ItineraryDetailsItemsListActions.vue";
import SupplierItiNotesDialog from "./supplier-notes/SupplierItiNotesDialog.vue";
import randomize from "randomatic";
const moment = require("moment");
export default {
  name: "itinerary-details-items-list",

  props: {
    itinerary: {
      type: Object,
      default: () => null,
    },
    refNameColumns: {
      type: String,
      default: () => "iti-details-items-list-cols",
    },
  },

  mixins: [SupplierTypeIconMixin, ListMixin, AgGridListMixin],

  components: {
    ListVisibleColumnsAndFilter,
    AgGridVue,
    AgGridSwitch,
    ListGrouped,
    ItiDetailsItemsTable,
    RemarksEditDialog,
    ItemPricingDialog,
    ItineraryDetailsItemsListActions,
    SupplierItiNotesDialog,
  },

  data() {
    return {
      debug: false,
      pagingOptions,
      search: "",
      expanded: [],
      totalsFields: [
        { name: "profit", label: "Total Gross Profit" },
        { name: "netProfit", label: "Net Profit" },
        { name: "toms", label: "TOMS" },
      ],
      visibleColumns: this.$store.getters.localSettings(this.refNameColumns, [
        "serviceTypeName",
        "startDate",
        "supplierName",
        "serviceName",
        "totalCost",
      ]),
      headers: [
        //http://tracker.globetrack.ie/issues/5658
        { text: "Order", value: "orderNo", name: "orderNo" },
        { text: "Type", value: "serviceTypeName", name: "serviceTypeName" },
        {
          text: "From",
          value: "startDate",
          name: "startDate",
          sort: sortByDate,
          comparator: sortByDateWithFormat(
            this.$store.getters.userConfig.dateFormat
          ),
          valueGetter: (payload) =>
            this.$options.filters.formatDate(payload.data.startDate),
        },
        {
          text: "To",
          value: "endDate",
          name: "endDate",
          sort: sortByDate,
          comparator: sortByDateWithFormat(
            this.$store.getters.userConfig.dateFormat
          ),
          valueGetter: (payload) =>
            this.$options.filters.formatDate(payload.data.endDate),
        },
        {
          text: "Duration",
          value: "duration",
          name: "duration",
        },
        { text: "Qty", value: "qty", name: "qty" },
        { text: "Duration", value: "duration", name: "duration" },
        { text: "Pax", value: "pax", name: "pax" },
        { text: "Supplier", value: "supplierName", name: "supplierName" },
        {
          text: "Supplier Destination",
          value: "supplierDestination",
          name: "supplierDestination",
        },
        { text: "Service", value: "serviceName", name: "serviceName" },
        {
          text: "Service Alt Name",
          value: "serviceAltName",
          name: "serviceAltName",
        },
        {
          text: "Cost",
          value: "pricing.totalBuy",
          name: "totalCost",
          valueGetter: (payload) => {
            return this.$options.filters.priceLabeler(
              payload.data.pricing.totalBuy
            );
          },
        },
        {
          text: "Discount (Cost)",
          value: "pricing.discountCost",
          name: "discountCost",
          valueGetter: (payload) => {
            return this.$options.filters.priceLabeler(
              payload.data.pricing.discountCost
            );
          },
        },
        {
          text: "CDiscount (Cost)",
          value: "pricing.circuitDiscountCost",
          name: "circuitDiscountCost",
          valueGetter: (payload) => {
            return this.$options.filters.priceLabeler(
              payload.data.pricing.circuitDiscountCost
            );
          },
        },
        {
          text: "Cost Currency",
          value: "pricing.currencyBuy",
          name: "currencyBuy",
          valueGetter: (payload) => {
            return this.$options.filters.priceLabeler(
              payload.data.pricing.currencyBuy
            );
          },
        },
        {
          text: "Cost ROE",
          value: "pricing.currencyBuyExchangeRate",
          name: "currencyBuyExchangeRate",
          valueGetter: (payload) => {
            return this.$options.filters.priceLabeler(
              payload.data.pricing.currencyBuyExchangeRate
            );
          },
        },
        {
          text: "Cost in Base",
          value: "totalCostInBase",
          name: "totalCostInBase",
        },
        { text: "Sell Price", value: "totalPrice", name: "totalPrice" },
        {
          text: "Sell ROE",
          value: "pricing.currencySellExchangeRate",
          name: "currencySellExchangeRate",
          valueGetter: (payload) => {
            return this.$options.filters.priceLabeler(
              payload.data.pricing.currencySellExchangeRate
            );
          },
        },
        {
          text: "Discount (Sell)",
          value: "pricing.discountSell",
          name: "discountSell",
          valueGetter: (payload) => {
            return this.$options.filters.priceLabeler(
              payload.data.pricing.discountSell
            );
          },
        },
        {
          text: "CDiscount (Sell)",
          value: "pricing.circuitDiscountSell",
          name: "circuitDiscountSell",
          valueGetter: (payload) => {
            return this.$options.filters.priceLabeler(
              payload.data.pricing.circuitDiscountSell
            );
          },
        },
        {
          text: "Sell in Base",
          value: "totalSellInBase",
          name: "totalSellInBase",
          valueGetter: (payload) =>
            this.$options.filters.priceLabeler(payload.data.totalSellInBase),
        },

        { text: "Gross Profit", value: "profit", name: "profit" },
        { text: "Agent Commission", value: "commission", name: "commission" },
        {
          text: "Supplier Commission",
          value: "supplierCommissionValue",
          name: "supplierCommissionValue",
        },
        {
          text: "Business Model",
          value: "businessModel",
          name: "businessModel",
        },
        { text: "Net Profit", value: "netProfit", name: "netProfit" },
        { text: "TOMS", value: "toms", name: "toms" },
        { text: "Net Margin %", value: "margin", name: "margin" },
        { text: "Net Markup %", value: "markup", name: "markup" },
        { text: "Status", value: "status", name: "status" },
        { text: "Rate Type", value: "rateTypeCode", name: "rateTypeCode" },
        { text: "Included", value: "isIncluded", name: "isIncluded" },
        { text: "Optional", value: "isOptional", name: "isOptional" },
        { text: "Hidden", value: "isHidden", name: "isHidden" },
        { text: "Historic", value: "historic", name: "historic" },
        { text: "Basis", value: "boardBasis", name: "boardBasis" },
        {
          text: "Actions",
          value: "actions",
          name: "actions",
          cellRendererFramework: "ItineraryDetailsItemsListActions",
        },
      ],
      showGroupedView: false,
      selectedGroupedBy: {
        name: "Supplier",
        getKey: (item) =>
          `${item.supplier ? item.supplier.id : ""}-${item.supplierName}`,
        getLabel: (item) => `Supplier: ${item.supplierName}`,
        reverse: true,
      },
      groupByOptions: [
        {
          name: "Supplier",
          getKey: (item) =>
            `${item.supplier ? item.supplier.id : ""}-${item.supplierName}`,
          getLabel: (item) => `Supplier: ${item.supplierName}`,
        },
        {
          name: "Supplier Type",
          getKey: (item) => item.serviceTypeName,
          getLabel: (item) => `${item.serviceTypeName}`,
        },
        {
          name: "Supplier Destination",
          getKey: (item) => item.supplierDestination,
          getLabel: (item) => `${item.supplierDestination}`,
        },
        {
          name: "Cost Currency",
          getKey: (item) => item.pricing.currencyBuy,
          getLabel: (item) => `${item.pricing.currencyBuy}`,
        },
        {
          name: "Day",
          getKey: (item) => item.startDate,
          getLabel: (item) =>
            `${this.$options.filters.formatDate(item.startDate)}`,
        },
      ],
      showRemarksEditor: false,
      selectedItem: null,
      showItemPricingEditor: false,
      showSupplierNotes: false,
    };
  },

  computed: {
    headerProps() {
      return this.itinerary
        ? {
            dateFrom: this.itinerary.dateFrom,
            dateTo: this.itinerary.dateTo,
          }
        : null;
    },
    gridHeight() {
      if (!this.fixedHeader) {
        return null;
      }
      return this.itemsWithExtras && this.itemsWithExtras.length >= 15
        ? this.windowHeight - 430
        : null;
    },
    sortBy() {
      return this.$store.getters.localSettings("itDetailsItemsSortBy");
    },
    csvData() {
      const rows = this.itemsWithExtras
        .filter((item) => item.isIncluded)
        .map((item) => {
          const row = {};
          for (const h of this.filteredHeaders) {
            if (item[h.value]) {
              if (h.value.indexOf("Date") > -1) {
                row[h.text] = this.$options.filters.formatDate(item[h.value]);
              } else {
                if (h.name == "serviceName" && item.isExtra) {
                  row[h.text] = `${item.boardBasis} (${item[h.value]})`;
                } else {
                  row[h.text] = item[h.value];
                }
              }
            } else {
              row[h.text] = getValueByPath(h.value, item);
            }
          }
          return row;
        });
      const lastRow = Object.assign({}, rows[rows.length - 1]);
      const totalsCols = [
        "Cost in Base",
        "Sell in Base",
        "Sell Price",
        "Net Profit",
        "Gross Profit",
        "Net Margin %",
        "Net Markup %",
        "Agent Commission",
      ];
      const profitCols = {
        "Net Profit": "netProfit",
        "Gross Profit": "grossProfit",
        "Net Margin %": "margin",
        "Net Markup %": "markup",
        "Agent Commission": "netCommission",
      };

      for (const k of Object.keys(lastRow)) {
        if (totalsCols.indexOf(k) == -1) {
          lastRow[k] = "";
        } else {
          if (Object.keys(profitCols).indexOf(k) > -1) {
            lastRow[k] = this.itinerary.profit[profitCols[k]];
          } else {
            lastRow[k] = getTotal(rows, (item) => Number(item[k]));
          }
        }
      }
      rows.push(lastRow);
      return rows;
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    list() {
      return this.itinerary && this.itinerary.items.edges
        ? this.itinerary.items.edges.map(removeGQLTypename).map((item) => ({
            ...item,
            // profit: Number(item.totalSellInBase - item.totalCostInBase).toFixed(
            //   2
            // ),
            // netProfit: Number(
            //   item.totalSellInBase -
            //     item.totalCostInBase -
            //     item.commission +
            //     item.supplierCommissionValue
            // ).toFixed(2),
            serviceAltName: item.service ? item.service.altName : "",
            adults: item.pax.adults,
            children: item.pax.children,
            infants: item.pax.infants,
            pax: `${item.pax.adults}/${item.pax.children}/${item.pax.infants}`,
            duration: itiItemDuration({
              ...item,
              serviceType: item.serviceTypeName,
              isExtra: false,
            }),
          }))
        : [];
    },
    itemsWithExtras() {
      return this.list.reduce((list, item) => {
        item.historic = item.yearShift != 0;
        list.push(item);
        if (item.extras) {
          for (const extra of item.extras) {
            //console.log("extra", extra);
            const extraService = {
              ...item,
              id: randomize("Aa0", 32),
              itemId: item.id,
              extraId: extra.id,
              isExtra: true,
              toms: extra.toms,
              pricing: {
                ...item.pricing,
                logs:
                  extra.pricing && extra.pricing.logs
                    ? [...extra.pricing.logs]
                    : [],
              },
              isHidden: extra.isHidden,
              status: extra.status,
              statusId: extra.statusId,
              currencyBuy: extra.currencyBuy,
              currencyBuyExchangeRate: extra.currencyBuyExchangeRate,
              totalPrice: extra.totalSell,
              profit: extra.profit,
              netProfit: extra.netProfit,
              commission: extra.commission,
              margin: extra.margin,
              markup: extra.markup,
              isIncluded: extra.isIncluded,
              isOptional: extra.isOptional,
            };

            extraService.boardBasis = extra.name;
            extraService.startDate = extra.startDate;
            extraService.endDate = extra.endDate;
            extraService.qty = extra.qty;
            extraService.totalCostInBase = extra.totalCostInBase;
            extraService.totalSellInBase = extra.totalSellInBase;
            extraService.price = extra.totalSell;
            //#8319
            // IF the extra is a per item rate type extra the pax numbers should match the parent service pax. - Christina Gore
            extraService.adults =
              extra.rateBase && extra.rateBase == "I"
                ? item.adults
                : extra.adults;
            extraService.children =
              extra.rateBase && extra.rateBase == "I"
                ? item.children
                : extra.children;
            extraService.infants =
              extra.rateBase && extra.rateBase == "I"
                ? item.infants
                : extra.infants;
            extraService.pricing.rateTypeCode = extra.rateTypeCode;
            extraService.pricing.totalBuy = extra.totalBuy;
            extraService.rateTypeCode = extra.rateTypeCode;
            //update pricing
            extraService.pricing.infantBuy = 0;
            extraService.pricing.infantSell = 0;
            extraService.pricing.childBuy = 0;
            extraService.pricing.childSell = 0;
            extraService.pricing.adultBuy = 0;
            extraService.pricing.adultSell = 0;
            extraService.pricing.itemBuy = 0;
            extraService.pricing.itemSell = 0;
            extraService.pricing.currencyBuy = extra.currencyBuy;
            extraService.pricing.currencyBuyExchangeRate =
              extra.currencyBuyExchangeRate;
            extraService.pricing.currencySellExchangeRate =
              extra.currencySellExchangeRate;

            extraService.pricing.discountCost = Number(extra.discountCost);
            extraService.pricing.discountSell = Number(extra.discountSell);
            //#8480
            extraService.pricing.circuitDiscountCost = Number(
              //https://tracker.globetrack.ie/issues/9538
              extra.circuitDiscountCost || 0
            );
            extraService.pricing.circuitDiscountSell = Number(
              //https://tracker.globetrack.ie/issues/9538
              extra.circuitDiscountSell || 0
            );
            extraService.passComp = 0;
            extraService.supplierComp = 0;
            const extraDuration = this.duration(extraService);
            //console.log(extra, extraDuration);
            if (extra.rateBase == "P") {
              if (extra.isChildOnly) {
                extraService.pricing.childBuy = extra.cost;
                // extra.percentPrice > 0
                //   ? extra.totalBuy / extra.children / extraDuration
                //   : extra.cost;
                extraService.pricing.childSell = extra.sell;
                // extra.percentPrice > 0
                //   ? extra.totalSell / extra.children / extraDuration
                //   : extra.sell;
              } else if (extra.isInfantOnly) {
                extraService.pricing.infantBuy = extra.cost;
                // extra.percentPrice > 0
                //   ? extra.totalBuy / extra.infants / extraDuration
                //   : extra.cost;
                extraService.pricing.infantSell = extra.sell;
                // extra.percentPrice > 0
                //   ? extra.totalSell / extra.children / extraDuration
                //   : extra.sell;
              } else {
                extraService.pricing.adultBuy = extra.cost;
                // extra.percentPrice > 0
                //   ? extra.totalBuy / extra.adults / extraDuration
                //   : extra.cost;
                extraService.pricing.adultSell = extra.sell;
                // extra.percentPrice > 0
                //   ? extra.totalSell / extra.adults / extraDuration
                //   : extra.sell;
              }
            } else {
              extraService.pricing.itemBuy = extra.cost;
              // extra.percentPrice > 0
              //   ? extra.totalBuy / extra.qty
              //   : extra.cost;
              extraService.pricing.itemSell = extra.sell;
              // extra.percentPrice > 0
              //   ? extra.totalSell / extra.qty
              //   : extra.sell;
            }
            extraService.includeCommission =
              extra.commission || extra.includeCommission;
            extraService.customPricing = extra.customPricing;
            extraService.remarks = { ...extra.remarks };
            extraService.orderNo = extra.orderNo
              ? parseInt(extra.orderNo)
              : item.orderNo;
            list.push(extraService);
          }
        }
        return list;
      }, []);
    },
    totalSellInBase() {
      return getTotal(
        this.itemsWithExtras.filter((item) => item.isIncluded),
        (item) => item.totalSellInBase
      );
    },
    totalCostInBase() {
      return getTotal(
        this.itemsWithExtras.filter((item) => item.isIncluded),
        (item) => item.totalCostInBase
      );
    },
    totalToms() {
      return getTotal(
        this.itemsWithExtras.filter((item) => item.isIncluded),
        (item) => item.toms
      );
    },
  },

  methods: {
    duration(item) {
      let start = moment(item.startDate).startOf("day");
      let end = moment(item.endDate).startOf("day");
      if (start.isValid() && end.isValid()) {
        let d = end.diff(start, "days");
        let addDay =
          item.serviceType != "ACCOMMODATION" || item.isExtra ? 1 : 0;
        //let addDay = 1;
        if (item.serviceType == "TRANSPORT") {
          //http://tracker.globetrack.ie/issues/5905
          // only if both pickup and dropoff times are set
          if (
            item.remarks &&
            item.remarks.pickupTime &&
            item.remarks.pickupTime != "" &&
            item.remarks.dropoffTime &&
            item.remarks.dropoffTime != ""
          ) {
            const pickupTime =
              item.remarks &&
              item.remarks.pickupTime &&
              item.remarks.pickupTime != ""
                ? item.remarks.pickupTime
                : "00:00";
            const dropoffTime =
              item.remarks &&
              item.remarks.dropoffTime &&
              item.remarks.dropoffTime != ""
                ? item.remarks.dropoffTime
                : "00:01";
            addDay =
              Number(dropoffTime.replace(/\D/, "")) >
              Number(pickupTime.replace(/\D/, ""))
                ? 1
                : 0;
          }
        }
        return d == 0 ? 1 : d + addDay;
      }
      return 1;
    },
    onSupplierNotes() {
      this.showSupplierNotes = true;
    },
    onShowItemPricingEditor(payload) {
      console.log("item", payload);
      this.selectedItem = {
        ...fromItineryItem({
          node: {
            ...payload,
            pax: {
              adults: payload.adults,
              children: payload.children,
              infants: payload.infants,
            },
          },
        }),
        ...payload,
      };
      console.log("item", this.selectedItem);
      this.showItemPricingEditor = true;
    },
    onShowRemarks(payload) {
      this.selectedItem = { ...payload };
      this.showRemarksEditor = true;
    },
    expandExtras(item) {
      const isExpanded = this.expanded.find((i) => i.id == item.id);

      this.expanded = isExpanded
        ? [...this.expanded.filter((i) => i.id != item.id)]
        : [...this.expanded, item];
    },
    extraQtyLabeler(e) {
      return extraDisplayQty(e);
    },
  },
};
</script>

<style scoped>
#ag-it-details-items-list {
  height: calc(100vh - 300px);
  min-height: 500px;
}
</style>



// WEBPACK FOOTER //
// src/components/gt/itineraries/ItineraryDetailsItemsList.vue